import { useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { ArrowLeft, Eye, Globe, ThumbsDown, ThumbsUp } from 'lucide-react';
import { useDeleteKbArticle, useKbArticle, useKbCategories, useSaveKbArticle } from '@/api/kb';
import { useAllUsers } from '@/api/users';
import type { KbArticle, KbCategory } from '@/api/types';
import { Badge, Button, Card, ErrorState, Input, Select, Spinner, Textarea, useToast } from '@/components/ui';
import { useAuth } from '@/app/auth/useAuth';
import { cn } from '@/lib/cn';
import { formatDateTime, formatNumber, humanize } from '@/lib/format';
import { ARTICLE_TONE } from './meta';
import { Markdown } from './Markdown';

export function ArticlePage() {
  const { articleId } = useParams();
  const isNew = !articleId;
  const article = useKbArticle(articleId);
  const categories = useKbCategories();
  const { can } = useAuth();

  if ((!isNew && article.isPending) || categories.isPending) {
    return (
      <div className="flex justify-center py-24">
        <Spinner size="lg" label="Loading article" />
      </div>
    );
  }
  const error = article.error ?? categories.error;
  if (error) {
    return (
      <Card>
        <ErrorState error={error} onRetry={() => void (article.refetch(), categories.refetch())} />
      </Card>
    );
  }

  const a = isNew ? null : article.data!;
  return (
    <div className="mx-auto max-w-5xl">
      <Link
        to="/kb"
        className="mb-4 inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900"
      >
        <ArrowLeft className="h-4 w-4" /> Knowledge base
      </Link>
      {a?.sourceUrl && (
        <p className="mb-4 flex flex-wrap items-center gap-1.5 rounded-xl bg-sky-50 px-4 py-2.5 text-sm text-sky-900 ring-1 ring-sky-200">
          <Globe className="h-4 w-4 shrink-0" /> Imported from
          <a
            href={a.sourceUrl}
            target="_blank"
            rel="noreferrer noopener"
            className="truncate font-medium underline"
          >
            {a.sourceUrl}
          </a>
          — edits are replaced the next time the website is re-imported.
        </p>
      )}
      {can('update', 'KnowledgeBase') ? (
        <ArticleEditor key={a?.id ?? 'new'} article={a} categories={categories.data ?? []} />
      ) : (
        a && <ArticleView article={a} category={categories.data?.find((c) => c.id === a.categoryId)} />
      )}
    </div>
  );
}

function ArticleView({ article: a, category }: { article: KbArticle; category?: KbCategory }) {
  return (
    <Card className="p-6 sm:p-8">
      <p className="text-brand-600 text-xs font-semibold tracking-wide uppercase">{category?.name}</p>
      <h1 className="mt-1 text-2xl font-bold">{a.title}</h1>
      <div className="mt-6">
        <Markdown source={a.body} />
      </div>
      <ArticleStats article={a} />
    </Card>
  );
}

function ArticleStats({ article: a }: { article: KbArticle }) {
  return (
    <div className="mt-8 flex flex-wrap gap-x-5 gap-y-1 border-t border-slate-100 pt-4 text-xs text-slate-500">
      <span className="flex items-center gap-1">
        <Eye className="h-3.5 w-3.5" aria-hidden /> {formatNumber(a.views)} views
      </span>
      <span className="flex items-center gap-1">
        <ThumbsUp className="h-3.5 w-3.5" aria-hidden /> {a.helpfulCount}
      </span>
      <span className="flex items-center gap-1">
        <ThumbsDown className="h-3.5 w-3.5" aria-hidden /> {a.notHelpfulCount}
      </span>
      <span>Updated {formatDateTime(a.updatedAt)}</span>
    </div>
  );
}

const schema = z.object({
  title: z.string().trim().min(3, 'At least 3 characters').max(200),
  categoryId: z.string().min(1, 'Pick a category'),
  status: z.enum(['draft', 'published', 'archived']),
  tags: z.string(),
  excerpt: z.string().trim().max(300),
  body: z.string().max(100_000),
});
type FormValues = z.infer<typeof schema>;

function ArticleEditor({ article, categories }: { article: KbArticle | null; categories: KbCategory[] }) {
  const toast = useToast();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const save = useSaveKbArticle();
  const remove = useDeleteKbArticle();
  const users = useAllUsers();
  const [tab, setTab] = useState<'write' | 'preview'>('write');
  const [confirmDelete, setConfirmDelete] = useState(false);
  const author = article?.authorId ? users.data?.find((u) => u.id === article.authorId) : undefined;

  const {
    register,
    handleSubmit,
    watch,
    reset,
    formState: { errors, isDirty },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      title: article?.title ?? '',
      categoryId: article?.categoryId ?? params.get('category') ?? categories[0]?.id ?? '',
      status: article?.status ?? 'draft',
      tags: article?.tags.join(', ') ?? '',
      excerpt: article?.excerpt ?? '',
      body: article?.body ?? '',
    },
  });

  const submit = (status?: FormValues['status']) =>
    handleSubmit((v) => {
      const input = {
        title: v.title,
        categoryId: v.categoryId,
        status: status ?? v.status,
        excerpt: v.excerpt,
        body: v.body,
        tags: [
          ...new Set(
            v.tags
              .split(/[\s,]+/)
              .map((t) => t.trim().toLowerCase())
              .filter(Boolean),
          ),
        ],
      };
      save.mutate(
        { id: article?.id, input },
        {
          onSuccess: (saved) => {
            reset({ ...v, status: input.status });
            toast.success(input.status === 'published' ? 'Article published' : 'Article saved');
            if (!article) navigate(`/kb/articles/${saved.id}`, { replace: true });
          },
          onError: (e) => toast.error(e.message),
        },
      );
    })();

  const status = watch('status');
  const body = watch('body');

  return (
    <form
      noValidate
      onSubmit={(e) => {
        e.preventDefault();
        void submit();
      }}
      className="flex flex-col gap-5 lg:flex-row"
    >
      <Card className="min-w-0 flex-1 p-5">
        <label htmlFor="article-title" className="sr-only">
          Title
        </label>
        <input
          id="article-title"
          placeholder="Article title"
          aria-invalid={!!errors.title || undefined}
          className="font-heading w-full border-0 bg-transparent text-2xl font-bold text-slate-900 placeholder:text-slate-300 focus:outline-none"
          {...register('title')}
        />
        {errors.title && (
          <p role="alert" className="mt-1 text-xs text-red-600">
            {errors.title.message}
          </p>
        )}
        <div role="tablist" aria-label="Editor mode" className="mt-4 flex gap-1 border-b border-slate-200">
          {(['write', 'preview'] as const).map((t) => (
            <button
              key={t}
              type="button"
              role="tab"
              aria-selected={tab === t}
              onClick={() => setTab(t)}
              className={cn(
                '-mb-px border-b-2 px-3 py-2 text-sm font-medium',
                tab === t
                  ? 'border-brand-500 text-brand-700'
                  : 'border-transparent text-slate-500 hover:text-slate-800',
              )}
            >
              {t === 'write' ? 'Write' : 'Preview'}
            </button>
          ))}
        </div>
        {tab === 'write' ? (
          <Textarea
            aria-label="Article body (Markdown)"
            rows={20}
            placeholder={'Write in Markdown. ## Headings, **bold**, - lists, [links](https://…)'}
            className="mt-3 font-mono text-[13px]"
            {...register('body')}
          />
        ) : (
          <div className="mt-4 min-h-[20rem]">
            {body.trim() ? (
              <Markdown source={body} />
            ) : (
              <p className="text-sm text-slate-400">Nothing to preview yet.</p>
            )}
          </div>
        )}
        {article && <ArticleStats article={article} />}
      </Card>

      <div className="space-y-4 lg:w-72 lg:shrink-0">
        <Card className="space-y-4 p-5">
          <div className="flex items-center justify-between">
            <span className="text-sm font-bold">Status</span>
            <Badge tone={ARTICLE_TONE[status]}>{humanize(status)}</Badge>
          </div>
          <Select label="Category" error={errors.categoryId?.message} {...register('categoryId')}>
            {categories.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </Select>
          <Select label="Visibility" {...register('status')}>
            <option value="draft">Draft — agents only</option>
            <option value="published">Published</option>
            <option value="archived">Archived</option>
          </Select>
          <Input label="Tags" placeholder="payout, tds" hint="Comma separated" {...register('tags')} />
          <Textarea
            label="Summary"
            rows={3}
            hint="Shown in search results. Leave blank to use the first paragraph."
            error={errors.excerpt?.message}
            {...register('excerpt')}
          />
          <div className="flex flex-col gap-2 pt-1">
            {status !== 'published' && (
              <Button type="button" loading={save.isPending} onClick={() => void submit('published')}>
                Publish
              </Button>
            )}
            <Button
              type="submit"
              variant={status === 'published' ? 'primary' : 'secondary'}
              loading={save.isPending}
              disabled={!!article && !isDirty}
            >
              {article ? 'Save changes' : 'Save draft'}
            </Button>
          </div>
        </Card>
        {article && (
          <Card className="space-y-2 p-5 text-xs text-slate-500">
            <p>Author: {author?.name ?? '—'}</p>
            <p>Created {formatDateTime(article.createdAt)}</p>
            {article.publishedAt && <p>Published {formatDateTime(article.publishedAt)}</p>}
            <p className="font-mono">/{article.slug}</p>
            <Button
              type="button"
              size="sm"
              variant={confirmDelete ? 'danger' : 'ghost'}
              className="mt-2 w-full"
              loading={remove.isPending}
              onClick={() =>
                confirmDelete
                  ? remove.mutate(article.id, {
                      onSuccess: () => (toast.success('Article deleted'), navigate('/kb')),
                      onError: (e) => toast.error(e.message),
                    })
                  : setConfirmDelete(true)
              }
            >
              {confirmDelete ? 'Confirm delete' : 'Delete article'}
            </Button>
          </Card>
        )}
      </div>
    </form>
  );
}
